import { Suspense, useEffect } from "react";
import ReactGA from "react-ga4";
import { useSelector } from "react-redux";
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from "react-router-dom";

import { fetchProfile, getAuthIsReady, getAuthUserData } from "@/entities/Auth";
import { fetchDetails } from "@/entities/Subscription";
import { CHUNK_AUTO_REFRESH } from "@/shared/const/storageKeys";
import { isErrorLike } from "@/shared/lib/helpers/isErrorLike";
import { useAppDispatch } from "@/shared/lib/hooks/redux";
import { useUtmFields } from "@/shared/lib/hooks/useUtmFields";
import { Spinner, SpinnerView } from "@/shared/ui/Spinner";

import { GOOGLE_ANALYTICS, SENTRY_DSN } from "./const/app";
import { AppRouter } from "./providers/Router";

import "./styles/index.scss";

import * as Sentry from "@sentry/react";

ReactGA.initialize(GOOGLE_ANALYTICS);

Sentry.init({
  debug: __IS_DEV__,
  environment: __IS_DEV__ ? "development" : "production",
  enabled: !__IS_DEV__,
  dsn: SENTRY_DSN,
  release: __SENTRY_RELEASE__,
  integrations: [
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
  ],
  tracesSampleRate: 0.1,
  profilesSampleRate: 0.1,
  ignoreErrors: ["Failed to fetch", "Fetch is aborted", "AbortError: The user aborted a request."],
  beforeSend(event, hint) {
    const error = hint?.originalException;

    // check if this is a chunk load error
    const isChunkError = isErrorLike(error) && /Loading (CSS )?chunk [\d]+ failed/i.test(error.message);

    const hasRefreshed = sessionStorage.getItem(CHUNK_AUTO_REFRESH);

    // don't report on first failure
    if (isChunkError && !hasRefreshed) {
      return null;
    }

    return event;
  },
});

export const App = () => {
  const dispatch = useAppDispatch();

  const isAuthReady = useSelector(getAuthIsReady);
  const userData = useSelector(getAuthUserData);

  useUtmFields();

  useEffect(() => {
    const abortController = new AbortController();

    dispatch(fetchProfile({ abortController }));

    return () => {
      abortController.abort();
    };
  }, [dispatch]);

  useEffect(() => {
    if (userData) {
      Sentry.setUser({
        id: userData.id,
        email: userData.email,
      });

      if (!userData.isWriter) {
        dispatch(fetchDetails());
      }
    } else {
      Sentry.setUser(null);
    }
  }, [dispatch, userData]);

  return (
    <Suspense fallback={<Spinner view={SpinnerView.Centered} />}>
      {isAuthReady ? <AppRouter /> : <Spinner view={SpinnerView.Centered} />}
    </Suspense>
  );
};
